/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <Not needed> */
"use client";

import { usePathname, useRouter } from "next/navigation";
import {
  createContext,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo
} from "react";

import type { NavigatorContextType, NavigatorProviderProps } from "./types";

const NavigatorContext = createContext<NavigatorContextType | undefined>(
  undefined
);

const RouteChangeHandler = () => {
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    globalThis.window.history.pushState(
      null,
      "",
      globalThis.window.location.href
    );

    const handlePopState = () => {
      try {
        // Usamos go(1) para recuperar la navegación de forma robusta ante doble clicks en Chrome
        globalThis.window.history.go(1);
      } catch {
        // Fallback seguro para Firefox o bloqueos de seguridad
        globalThis.window.history.pushState(
          null,
          "",
          globalThis.window.location.href
        );
      }
      console.info("Navegación por botones del navegador deshabilitada");
    };

    globalThis.window.addEventListener("popstate", handlePopState);

    return () => {
      globalThis.window.removeEventListener("popstate", handlePopState);
    };
  }, [pathname, router]);

  return null;
};

const NavigatorProvider = ({ children }: NavigatorProviderProps) => {
  const router = useRouter();

  const navigate = useCallback(
    (path: string) => router.replace(path),
    [router]
  );
  const goBack = useCallback(() => router.back(), [router]);
  const replace = useCallback((path: string) => router.replace(path), [router]);
  const reload = useCallback(() => globalThis.window.location.reload(), []);
  const refresh = useCallback(() => router.refresh(), [router]);

  const value = useMemo(
    () => ({
      navigate,
      goBack,
      replace,
      reload,
      refresh
    }),
    [navigate, goBack, replace, reload, refresh]
  );

  return (
    <NavigatorContext.Provider value={value}>
      <Suspense fallback={null}>
        <RouteChangeHandler />
      </Suspense>
      {children}
    </NavigatorContext.Provider>
  );
};

export const useNavigator = () => {
  const context = useContext(NavigatorContext);
  if (!context) {
    throw new Error("useNavigator must be used within a NavigatorProvider");
  }
  return context;
};

export default NavigatorProvider;
